import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowRight, Send } from 'lucide-react';
import type { ConfidencePolicy, ConfidenceSchema } from './field-confidence-holds';
import {
  contentStamp,
  flaggedIds,
  flaggedReasonWords,
  flaggedReasons,
  REGROUPED_WORDS,
  refusedWords,
  regroupedReasons,
  type FlaggedDocument,
  type FlaggedView,
} from './flagged-after-export';

/** The adapter's read (flagged-after-export.ts, `flaggedCommands`). */
type ListFlagged = (request: { jobId: string; batchId?: string }) => Promise<FlaggedDocument[]>;

/** The open batch's flagged documents, and whether a document on screen is one of them. */
export interface FlaggedAfterExportState {
  ids: ReadonlySet<string>;
  /** Exported and flagged now (the engine's latest answer, or the ledger's): its Confirm sends nothing again. */
  exported: (document: FlaggedView | null | undefined) => boolean;
  /** Why a regrouping left it for a person (the ledger's reason, `REGROUPED_WORDS`); null otherwise. */
  regrouped: (document: FlaggedView | null | undefined) => string | null;
  /** Where a Confirm sends it, a destination having refused it ("Will be sent to …"); null otherwise. */
  sendsTo: (document: FlaggedView | null | undefined) => string | null;
}

/**
 * Reads the flagged documents of the batch on screen: when the batch opens, when its list changes
 * (a split, a join, an undo), when the open document's pages or values do (a page removed or
 * restored, a value corrected) and when its flag does (a Confirm clears it). Never polled.
 */
export function useFlaggedAfterExport(
  jobId: string,
  batch: { batchId: string; documents: readonly FlaggedView[] } | null | undefined,
  loaded: ReadonlySet<string>,
  open: FlaggedView | null | undefined,
): FlaggedAfterExportState {
  const [listed, setListed] = useState<{ key: string; flags: FlaggedDocument[] }>(none);
  const batchId = batch?.batchId ?? '';
  const key = `${jobId}:${batchId}`;
  const stamp = listStamp(batch?.documents.length ?? 0, open);
  useEffect(() => {
    const list = (window.capturePointDesktop as unknown as { listFlaggedAfterExport?: ListFlagged })
      .listFlaggedAfterExport;
    if (!list || !batchId || batchId.startsWith('live:')) return undefined;
    let live = true;
    list({ jobId, batchId })
      .then((flags) => live && setListed({ key, flags: flags ?? [] }))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [jobId, batchId, key, stamp]);
  const ids = useMemo(
    () => flaggedIds(listed.key === key ? listed.flags : [], batch?.documents ?? [], loaded),
    [listed, key, batch?.documents, loaded],
  );
  const regrouped = useMemo(
    () => regroupedReasons(listed.key === key ? listed.flags : []),
    [listed, key],
  );
  const refused = useMemo(
    () => refusedWords(listed.key === key ? listed.flags : []),
    [listed, key],
  );
  return useMemo(() => stateOf(ids, regrouped, refused), [ids, regrouped, refused]);
}

const none = { key: '', flags: [] as FlaggedDocument[] };

/** What makes the list worth reading again: the batch's documents, the open one's flag, Confirm, pages and values. */
const listStamp = (documents: number, open: FlaggedView | null | undefined): string =>
  [documents, flaggedReasons(open).join(), open?.confirmedAsWhole ?? '', contentStamp(open)].join(
    ':',
  );

function stateOf(
  ids: ReadonlySet<string>,
  regrouped: ReadonlyMap<string, string>,
  refused: ReadonlyMap<string, string>,
): FlaggedAfterExportState {
  const exported = (document: FlaggedView | null | undefined) =>
    Boolean(document?.id) && !document?.confirmedAsWhole && ids.has(document!.id!);
  return {
    ids,
    exported,
    regrouped: (document) => (exported(document) ? (regrouped.get(document!.id!) ?? null) : null),
    sendsTo: (document) => (document?.id ? (refused.get(document.id) ?? null) : null),
  };
}

/**
 * Over the fields of an exported document: that it already went out, what the later check found
 * in the screen's own words, and that it is not sent again (ADR-0031). On one a regrouping left for
 * a person, one line instead: how the exported copy differs from what is on screen.
 * Nothing on any other document.
 */
export function FlaggedAfterExportNotice({
  document,
  schema,
  policy,
  exported,
  regrouped,
  sendsTo,
}: {
  document: FlaggedView | null | undefined;
  schema: ConfidenceSchema;
  policy: ConfidencePolicy;
  exported: boolean;
  regrouped?: string | null;
  sendsTo?: string | null;
}): ReactNode {
  if (document && sendsTo) return <RegroupedLine words={['Refused.', sendsTo]} />;
  if (!document || !exported) return null;
  const differs = regrouped ? REGROUPED_WORDS[regrouped] : undefined;
  if (differs) return <RegroupedLine words={differs} />;
  const reasons = flaggedReasonWords(document, schema, policy);
  return (
    <div className="paige-review-notice paige-flagged-notice" role="status">
      <Send size={14} aria-hidden="true" />
      <span>
        <strong>Already exported.</strong>{' '}
        {reasons.length > 0 ? 'A later check flagged it:' : 'It is not sent again.'}
        {reasons.length > 0 && (
          <>
            <ul>
              {reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
            <small>Corrections teach Cortexa; the exported copy is not sent again.</small>
          </>
        )}
      </span>
    </div>
  );
}

/** The one line for a document a regrouping left for a person (`REGROUPED_WORDS`). */
function RegroupedLine({ words }: { words: readonly [string, string] }): ReactNode {
  return (
    <div className="paige-review-notice paige-flagged-notice" role="status">
      <Send size={14} aria-hidden="true" />
      <span>
        <strong>{words[0]}</strong> {words[1]}
      </span>
    </div>
  );
}

/** Under the document filters: how many exported documents a later check flagged, and the next one. */
export function FlaggedAfterExportFinder({
  count,
  onNext,
}: {
  count: number;
  onNext: () => void;
}): ReactNode {
  if (count === 0) return null;
  return (
    <button
      type="button"
      className="paige-flagged-finder"
      onClick={onNext}
      title={`Open the next exported document a later check flagged (${count})`}
    >
      <Send size={13} aria-hidden="true" />
      <span>Exported, then flagged</span>
      <b>{count}</b>
      <ArrowRight size={13} aria-hidden="true" />
    </button>
  );
}

/** The small mark on a flagged document in the list. */
export function FlaggedAfterExportChip(): ReactNode {
  return (
    <span
      className="paige-held-by paige-flagged-chip"
      title="Exported, then flagged by a later check"
    >
      <Send size={11} aria-hidden="true" />
      <span>Exported, flagged</span>
    </span>
  );
}
